import { V4 } from '@moleculeone/utils';

import { allKeys, substractedKeys } from '../collections';
import { atomToNeighborsIndices, areBondsSeparate } from '../selectors';

// Measures undesirable overlap of groups and bad bond lengths.
// The higher score the better. Score >= 1 is considered totally satisfactory, worst score = 0.
export function overlapScore(mol, group1Pos, group2Pos, bonds = atomToNeighborsIndices(mol)) {
  // we ignore keys that are present in both groups, as currently we don't want to measure
  // whether positions of common atoms is the same in both groups.
  const g1Keys = substractedKeys(group1Pos, group2Pos);
  const g2Keys = substractedKeys(group2Pos, group1Pos);
  const g1set = new Set(g1Keys);
  const g2set = new Set(g2Keys);
  const g1bonds = [];
  const g2bonds = [];

  for (const bond of Object.values(mol.bonds)) {
    if (g1set.has(bond.start) && g1set.has(bond.end)) {
      g1bonds.push([bond, group1Pos[bond.start], group1Pos[bond.end]]);
    }
    if (g2set.has(bond.start) && g2set.has(bond.end)) {
      g2bonds.push([bond, group2Pos[bond.start], group2Pos[bond.end]]);
    }
  }

  let overlapScore = Number.POSITIVE_INFINITY;
  for (const k1 of g1Keys) {
    for (const k2 of g2Keys) {
      const dist = V4.dist(group1Pos[k1], group2Pos[k2]) / 100;
      // for non-bonded atoms, the greater the distance is the better.
      // if two atoms are bonded, the preferred distance is 1, not infty
      if (bonds[k1].includes(k2)) {
        // score > 1 for dist \in (0.78, 1.28), score = 0 for dist = 0 and dist -> infty
        overlapScore = Math.min(overlapScore, Math.sqrt(dist) / (4.0 * Math.abs(dist - 1)));
      } else {
        // dist = 2/3 is totally ok (avg bond dist = 1)
        overlapScore = Math.min(overlapScore, dist * 1.5);
      }
    }
  }

  for (const [bond1, s1, e1] of g1bonds) {
    for (const [bond2, s2, e2] of g2bonds) {
      // bonds may cross, but atoms should not be covered by bonds
      if (areBondsSeparate(bond1, bond2) && V4.segmentsCross(s1, e1, s2, e2)) {
        const dist = V4.pointsSegmentDist(s1, e1, s2, e2) / 100;
        overlapScore = Math.min(overlapScore, dist);
      }
    }
  }

  return overlapScore;
}

export function mergePositions(...positionsObjects) {
  const result = {};
  for (const atomIdx of allKeys(...positionsObjects)) {
    const positions = positionsObjects.map(group => group[atomIdx]).filter(position => position != null);
    result[atomIdx] = V4.average(...positions);
  }
  return result;
}

export function addGroup(positionsData, groupKey, positions) {
  const atomsIndices = Object.keys(positions);
  positionsData.groupGraph[groupKey] = atomsIndices;
  for (const idx of atomsIndices) {
    positionsData.groupGraph[idx].push(groupKey);
  }
  positionsData.groupPositions[groupKey] = positions;
}

export function mergeGroups(positionsData, groupsPositionsList, groupsIndices) {
  const mergedPositions = mergePositions(...groupsPositionsList);
  const mergedGroupIdx = groupsIndices[0];

  for (const groupIdx of groupsIndices) {
    delete positionsData.groupPositions[groupIdx];
    delete positionsData.groupGraph[groupIdx];
  }
  positionsData.groupPositions[mergedGroupIdx] = mergedPositions;
  positionsData.groupGraph[mergedGroupIdx] = Object.keys(mergedPositions);

  for (const atomIdx of Object.keys(mergedPositions)) {
    const currentGroup = positionsData.groupGraph[atomIdx];
    const updatedGroup = currentGroup.filter(groupIdx => !groupsIndices.includes(groupIdx));
    updatedGroup.push(mergedGroupIdx);
    positionsData.groupGraph[atomIdx] = updatedGroup;
  }
}

export function bondGroupKey(bondIdx) {
  return `g${bondIdx}`;
}

export function atomGroupKey(atomIdx) {
  return `ga${atomIdx}`;
}

export function tripleBondGroupKey(bondIdx) {
  return `gt${bondIdx}`;
}

export function geometryBondGroupKey(bondIdx) {
  return `gd${bondIdx}`;
}

export function isGroupKey(key) {
  return key.charAt(0) === 'g';
}

const frequency = arr => {
  return arr.reduce((prev, curr) => {
    if (!prev[curr]) prev[curr] = 0;
    prev[curr] += 1;
    return prev;
  }, {});
};

export const entropyDistribution = arr => {
  const fq = frequency(arr);
  const count = arr.length;

  const sumRevDet = Object.keys(fq).reduce((prev, curr) => {
    const localFq = fq[curr];
    const norm = localFq / count;
    return prev + norm * Math.log(norm);
  }, 0);

  return -sumRevDet;
};

export function shuffle(arr, random) {
  const result = [...arr];

  for (let i = result.length - 1; i >= 1; --i) {
    const j = Math.floor(random.quick() * (i + 1));

    const swap = result[i];
    result[i] = result[j];
    result[j] = swap;
  }

  return result;
}

const cycle = (arr, k) => {
  const result = [...arr];
  for (let i = 0; i < k; i++) {
    result.unshift(result.pop());
  }
  return result;
};

export const cyclePermutations = arr => {
  const result = [];
  for (let i = 0; i < arr.length; i++) {
    result.push(cycle(arr, i));
  }
  return result;
};

export const percent = (n, max) => (n / max) * 100;
